/** Apoio ao contexto que as telas publicam: monta o mapa de filtros só com o que
 *  está preenchido, escolhe o conjunto de perguntas sugeridas pela rota e dá o
 *  texto do chip acima do campo. */
import type { UseI18n } from '@/core/i18n/useI18n'
import type { ContextoDaTela } from './api'

export type { ContextoDaTela }

type ValorDeFiltro = string | string[] | null | undefined

/** Pares rótulo e valor viram `filtros` sem as entradas vazias; lista vira texto
 *  separado por vírgula. Undefined quando nada está preenchido, para o contexto
 *  não carregar um objeto vazio. */
export function filtrosPreenchidos(pares: Array<[string, ValorDeFiltro]>): Record<string, string> | undefined {
  const saida: Record<string, string> = {}
  for (const [rotulo, valor] of pares) {
    const textoDoValor = Array.isArray(valor) ? valor.filter(Boolean).join(', ') : (valor ?? '').trim()
    if (textoDoValor) saida[rotulo] = textoDoValor
  }
  return Object.keys(saida).length ? saida : undefined
}

/** Conjuntos de perguntas sugeridas no estado vazio, um por tela da seção 2 da
 *  interface. As chaves de tradução são `assistente.sugestoes.<conjunto>.<n>`. */
export type ConjuntoDeSugestoes =
  | 'jornada' | 'historico' | 'diarias' | 'frente' | 'viagens' | 'hospedagem'
  | 'cadastros' | 'mapa' | 'dashboard' | 'geral'

const ROTAS: Array<[prefixo: string, conjunto: ConjuntoDeSugestoes]> = [
  ['/logistica/jornada/historico', 'historico'],
  ['/logistica/jornada', 'jornada'],
  ['/logistica/diarias', 'diarias'],
  ['/logistica/shipments', 'frente'],
  ['/logistica/viagens', 'viagens'],
  ['/logistica/hospedagem', 'hospedagem'],
  // Os cadastros moravam todos sob /backoffice/cadastros até as áreas (24/10/2026),
  // Usuários e Cargos inclusive, e seguem com as mesmas sugestões.
  ['/administrativo/usuarios', 'cadastros'],
  ['/administrativo/cargos', 'cadastros'],
  ['/logistica/cadastros', 'cadastros'],
  ['/logistica/conjuntos', 'cadastros'],
  ['/logistica/mapa', 'mapa'],
  ['/logistica/dashboard', 'dashboard'],
  // Pendências morava sob o Dashboard, e segue com as mesmas sugestões.
  ['/logistica/pendencias', 'dashboard'],
]

export function conjuntoDeSugestoes(rota: string | null | undefined): ConjuntoDeSugestoes {
  if (!rota) return 'geral'
  for (const [prefixo, conjunto] of ROTAS) {
    if (rota === prefixo || rota.startsWith(`${prefixo}/`)) return conjunto
  }
  return 'geral'
}

const QUANTIDADE_DE_SUGESTOES = 4

/** As perguntas sugeridas traduzidas, três ou quatro por conjunto; chave sem
 *  tradução é pulada, então cada conjunto pode ter menos que quatro. */
export function sugestoesTraduzidas(t: UseI18n['t'], conjunto: ConjuntoDeSugestoes): string[] {
  const saida: string[] = []
  for (let n = 1; n <= QUANTIDADE_DE_SUGESTOES; n += 1) {
    const chave = `assistente.sugestoes.${conjunto}.${n}`
    const valor = t(chave)
    if (valor && valor !== chave) saida.push(valor)
  }
  return saida
}

/** "AAAA-MM-DD" → "16/08". O ano fica de fora do chip, que é estreito. */
export const diaCurto = (iso: string) => (iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : iso)

/** Texto do chip acima do campo: Falando sobre Painel de Jornada, 16/08 a 03/09. */
export function textoDoChip(t: UseI18n['t'], contexto: ContextoDaTela): string {
  const partes: string[] = []
  if (contexto.periodo) {
    partes.push(contexto.periodo.de === contexto.periodo.ate
      ? diaCurto(contexto.periodo.de)
      : t('assistente.contexto.periodo', { de: diaCurto(contexto.periodo.de), ate: diaCurto(contexto.periodo.ate) }))
  }
  if (contexto.entidade) partes.push(contexto.entidade.nome)
  return partes.length
    ? t('assistente.contexto.falandoSobreDetalhe', { tela: contexto.tela, detalhe: partes.join(', ') })
    : t('assistente.contexto.falandoSobre', { tela: contexto.tela })
}
